import { Link } from "react-router-dom";
import { Linkedin, Instagram, Youtube, Twitter } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const LOGO = "https://customer-assets.emergentagent.com/job_821563ca-1f8b-4664-98fd-7932004d145f/artifacts/r4nyzz7e_Raknati.png";

export default function Footer() {
  const { t, lang } = useI18n();
  const year = new Date().getFullYear();

  const productLinks = [
    { label: t("nav.how"), href: "#how" },
    { label: t("nav.features"), href: "#features" },
    { label: t("nav.solutions"), href: "#solutions" },
    { label: t("nav.pricing"), href: "#pricing" },
  ];

  const companyLinks = [
    { label: t("nav.partners"), href: "#partners" },
    { label: t("nav.investors"), href: "#investors" },
    { label: t("nav.faq"), href: "#faq" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  const scroll = (h) => {
    const el = document.querySelector(h);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-brand-charcoal text-white/90" data-testid="site-footer">
      <div className="container-x py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="Raknati" className="h-10 w-10 object-contain" />
              <div>
                <div className={`text-xl font-extrabold tracking-tight ${lang === "ar" ? "font-arabicHead" : ""}`}>
                  {lang === "ar" ? "ركنتي" : "Raknati"}
                </div>
                <div className="text-xs uppercase tracking-[0.24em] text-brand-gold">
                  {t("footer.tagline")}
                </div>
              </div>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-white/60 max-w-md">
              {t("footer.disclaimer")}
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                { icon: Linkedin, label: "LinkedIn", href: "#" },
                { icon: Instagram, label: "Instagram", href: "#" },
                { icon: Youtube, label: "YouTube", href: "#" },
                { icon: Twitter, label: "X", href: "#" },
              ].map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/15 hover:border-brand-gold hover:text-brand-gold transition-colors duration-200"
                  data-testid={`footer-social-${label.toLowerCase()}`}
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-gold mb-4">
              {t("footer.product")}
            </div>
            <ul className="space-y-3 text-sm text-white/75">
              {productLinks.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => scroll(l.href)}
                    className="hover:text-white transition-colors duration-200 text-start"
                    data-testid={`footer-link-${l.href.slice(1)}`}
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-gold mb-4">
              {t("footer.company")}
            </div>
            <ul className="space-y-3 text-sm text-white/75">
              {companyLinks.map((l) => (
                <li key={l.href}>
                  <button
                    onClick={() => scroll(l.href)}
                    className="hover:text-white transition-colors duration-200 text-start"
                    data-testid={`footer-link-${l.href.slice(1)}`}
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-gold mb-4">
              {t("footer.legal")}
            </div>
            <ul className="space-y-3 text-sm text-white/75">
              <li>
                <Link to="/privacy" className="hover:text-white transition-colors duration-200" data-testid="footer-privacy">
                  {t("footer.privacy")}
                </Link>
              </li>
              <li>
                <span className="opacity-70">{t("footer.terms")}</span>
              </li>
              <li>
                <span className="opacity-70">{t("footer.cookies")}</span>
              </li>
              <li>
                <span className="opacity-70">{t("footer.data_deletion")}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="text-xs text-white/50">
            © {year} Raknati. {t("footer.rights")}
          </div>
          <div className="text-xs text-white/50">
            United Arab Emirates · UAE
          </div>
        </div>
      </div>
    </footer>
  );
}
